<template>
  <div>
    <div class="row mt-2 no-gutters evollu-form" v-if="showFilters">

      <!--Date Filter-->
      <div class="row">
        <RangeDatePicker
          id="reports-date-picker"
          v-model="filters.dateRange"
          :placeholder="'---'"
          class="col-12 col-md-4 datesFilter"
          :clearable="false">
          <div slot="label">
            <label class="form-label" style="margin-top: -3px;">
              {{ $t('reports.reports.labels.date_period') }}
            </label>
          </div>
        </RangeDatePicker>
        <!--End Date Filter-->

        <!--Client Filters-->
        <div class="col-12 col-md-4">
          <label class="form-label">{{ $t('reports.reports.labels.client') }}:</label>
          <multiselect
            v-model="filters.clients"
            :options="clientOptions"
            track-by="uid"
            label="name"
            :custom-label="formatedEntityLabel"
            select-label=""
            deselect-label="×"
            :placeholder="$t('reports.reports.labels.multiselect.clients_placeholder')"
            :searchable="true"
            :allow-empty="clientOptions.length > 1"
            :multiple="clientOptions.length > 1"
          >
            <span slot="noResult">{{ $t('patients.noResults') }}</span>

            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="clientOptions.length > 1 && filters.clients.length > 0"
                   @mousedown.prevent.stop="filters.clients = []"></div>
            </template>

          </multiselect>
        </div>
        <!--End Client Filters-->

        <!-- Client Type Filter -->
        <div class="col-12 col-md-4" v-if="canUpdateReports">
          <label class="form-label">{{ $t('reports.reports.labels.client_type') }}:</label>
          <multiselect
            v-model="filters.clientType"
            :options="clientTypes"
            track-by="val"
            label="name"
            select-label=""
            deselect-label="×"
            :placeholder="$t('reports.reports.labels.multiselect.client_types_placeholder')"
            :searchable="true"
            :allow-empty="true"
            :multiple="false"
          >
            <span slot="noResult">{{ $t('patients.noResults') }}</span>

            <template slot="singleLabel" slot-scope="props">
              <span>{{ $t(props.option.name) }}</span>
            </template>

            <template slot="option" slot-scope="props">
              <span>{{ $t(props.option.name) }}</span>
            </template>

            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="filters.clientType"
                   @mousedown.prevent.stop="filters.clientType = null"></div>
            </template>

          </multiselect>
        </div>

        <!-- Report States Filters -->
        <div class="col-12 col-md-4" v-if="canUpdateReports">
          <label class="form-label">{{ $t('reports.reports.labels.report_state') }}:</label>
          <multiselect
            v-model="filters.locked"
            :options="reportStates"
            track-by="val"
            label="name"
            select-label=""
            deselect-label="×"
            :placeholder="$t('reports.reports.labels.multiselect.report_states_placeholder')"
            :searchable="true"
            :allow-empty="true"
            :multiple="false"
          >
            <span slot="noResult">{{ 'patients.noResults'| translate }}</span>

            <template slot="singleLabel" slot-scope="props">
              <span>{{ $t(props.option.name) }}</span>
            </template>

            <template slot="option" slot-scope="props">
              <span>{{ $t(props.option.name) }}</span>
            </template>

            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="filters.locked"
                   @mousedown.prevent.stop="filters.locked = null"></div>
            </template>

          </multiselect>
        </div>

        <!-- Invoice States Filters -->
        <div class="col-12 col-md-4">
          <label class="form-label">{{ $t('reports.reports.labels.invoice_state') }}:</label>
          <multiselect
            v-model="filters.invoiceState"
            :options="invoiceStates"
            track-by="val"
            label="name"
            select-label=""
            deselect-label="×"
            :placeholder="$t('reports.reports.labels.multiselect.invoice_states_placeholder')"
            :searchable="true"
            :allow-empty="true"
            :multiple="false"
          >
            <span slot="noResult">{{ $t('patients.noResults') }}</span>

            <template slot="singleLabel" slot-scope="props">
              <span>{{ $t(props.option.name) }}</span>
            </template>

            <template slot="option" slot-scope="props">
              <span>{{ $t(props.option.name) }}</span>
            </template>

            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="filters.invoiceState"
                   @mousedown.prevent.stop="filters.invoiceState = null"></div>
            </template>

          </multiselect>
        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">{{ $t('reports.results') }}</label>
          <va-checkbox
            :label="$t('reports.hide_results_0_euro')"
            :id="'checkbox-hide-zeros'"
            v-model="filters.hideReportsWithZeros"/>
        </div>

      </div>
    </div>
    <div>
      <v-server-table
        id="reportsTable"
        ref="reportsTable"
        :name="'reportsTable'"
        :columns="columns"
        :options="tableOptions"
        @filter="onFilterUpdate"
        @loaded="onLoaded"
        @loading="onLoading"
        @row-click="event => update(event.row)">

      <span slot="updated_at" slot-scope="props">
              {{ props.row.updated_at | localTime('YYYY-MM-DD HH:mm') }}
            </span>
        <span slot="start_date" slot-scope="props">
              {{ props.row.start_date | localTime('YYYY-MM-DD') }}
            </span>
        <span slot="end_date" slot-scope="props">
              {{ props.row.end_date | localTime('YYYY-MM-DD') }}
            </span>
        <span slot="client" slot-scope="props">
              {{ presentClient(props.row.client) || $t('reports.reports.labels.no_facilities') }}
            </span>
        <span slot="client_type" slot-scope="props">
              {{ clientTypesAdapter[props.row.client_type].name | translate }}
            </span>
        <span slot="is_control" slot-scope="props">
              <span>{{ props.row.is_control ? $t('yes') : $t('no') }}</span>
            </span>
        <span slot="total_value" slot-scope="props">
              {{ presentTotalValue(props.row.total_value) }}&nbsp;<i class="fa fa-euro"></i>
            </span>

        <span slot="locked" slot-scope="props" class="text-center w-100 d-inline-block">
        <va-popover v-if="props.row.locked" :message="$t('reports.reports.buttons.report_locked')" placement="top">
          <i class="report-action-icon fa fa-check p-2"></i>
        </va-popover>
        <va-popover v-else :message="$t('reports.reports.buttons.report_unlocked')" placement="top">
          <i class="report-action-icon fa fa-clock-o p-2"></i>
        </va-popover>
      </span>

        <span slot="invoiced" slot-scope="props">
        <va-popover v-if="props.row.invoice_number !== null"
                    :message="$t('reports.reports.buttons.report_invoice', {invoice: props.row.invoice_number, date: $options.filters.formattedDateString(props.row.invoice_date, 'YYYY-MM-DD')})"
                    placement="top">
          <i class="fa fa-check "></i>
        </va-popover>
        <va-popover v-else :message="$t('reports.reports.buttons.report_not_invoiced')" placement="top">
          <i class="report-action-icon fa fa-clock-o p-2"></i>
        </va-popover>
      </span>

        <span slot="payed" slot-scope="props">
        <va-popover v-if="props.row.payment_date !== null"
                    :message="$t('reports.reports.buttons.report_payment', {payment_date: moment(props.row.payment_date).format('YYYY-MM-DD')})"
                    placement="top">
          <i class="fa fa-check "></i>
        </va-popover>
        <va-popover v-else :message="$t('reports.reports.buttons.report_not_payed')" placement="top">
          <i class="report-action-icon fa fa-clock-o p-2"></i>
        </va-popover>
      </span>

        <span slot="actions" slot-scope="props" class="w-100 d-flex justify-content-center">
          <va-popover :message="$t('reports.reports.buttons.download_report')" placement="top">
            <i class="report-action-icon fa fa-download p-2" @click.prevent.stop="download(props.row)"></i>
          </va-popover>
          <va-popover v-if="canUpdateReport(props.row)" :message="$t('reports.reports.buttons.update_report')"
                      placement="top">
            <i class="report-action-icon fa fa-pencil p-2" @click.prevent.stop="update(props.row)"></i>
          </va-popover>
          <va-popover v-if="canDeleteReport(props.row)" :message="$t('reports.reports.buttons.delete_report')"
                      placement="top">
            <i class="report-action-icon fa fa-trash-o p-2" @click.prevent.stop="deleteReport(props.row)"></i>
          </va-popover>
      </span>

      </v-server-table>
    </div>
    <UpdateReport
      v-if="selectedReportForUpdate"
      :serviceUrl="serviceUrl"
      :report="selectedReportForUpdate"
      @update="onReportUpdate"
      @modalWasClosed="updateModalWasClosed"
    />

    <ConfirmationModal v-if="selectedReportForDeletion"
                       :title="$t('reports.reports.titles.delete_report')"
                       :body="$t('reports.reports.messages.delete_modal_body', {name: selectedReportForDeletion.name})"
                       okClass="btn-danger"
                       @modal-confirmed="confirmDelete"
                       @modal-canceled="cancelDelete"
    >
    </ConfirmationModal>
  </div>
</template>

<script>
import TableQueryMixin from '@/mixins/TableQueryMixin'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import { ReportsService } from '@/api/ReportsService'
import Multiselect from 'vue-multiselect'
import { mapGetters } from 'vuex'
import UpdateReport from '@/components/reports/reports/UpdateReport'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { TABLE_REPORTS } from '@/local-storage/LocalStorageKeys'
import { has } from 'lodash'

const reportsInfoType = 210

export default {
  components: {
    ConfirmationModal,
    UpdateReport,
    RangeDatePicker,
    Multiselect
  },
  mixins: [
    TableQueryMixin,
    MultiselectLabelFormatter,
    ClustersAndEntitiesMixin
  ],
  props: {
    showFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    querySearch: {
      type: String,
      required: false
    },
    customQueries: {
      type: Object,
      required: false,
      default: null
    },
    serviceUrl: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      queryUrl: `${this.$store.state.reports_server}/reports`,
      entities: [],
      filters: {
        hideReportsWithZeros: true,
        querySearch: '',
        clients: [],
        clientType: null,
        locked: null,
        invoiceState: null,
        dateRange: {
          start: null,
          end: null
        }
      },
      invoiceStates: [
        { id: 1, name: this.$t('reports.reports.labels.invoice_states.invoiced'), val: 1 },
        { id: 2, name: 'reports.reports.labels.invoice_states.not_invoiced', val: 2 },
        { id: 3, name: 'reports.reports.labels.invoice_states.paid', val: 3 },
        { id: 4, name: 'reports.reports.labels.invoice_states.not_paid', val: 4 }
      ],
      clientTypesAdapter: {
        1: { name: 'reports.reports.labels.client_types.client' },
        2: { name: 'reports.reports.labels.client_types.provider' }
      },
      clientTypes: [
        { val: 1, name: 'reports.reports.labels.reports_for_client' },
        { val: 2, name: 'reports.reports.labels.reports_for_provider' }
      ],
      reportStates: [
        { val: false, name: 'reports.reports.labels.unlocked_reports' },
        { val: true, name: 'reports.reports.labels.locked_reports' }
      ],
      tableOptions: {
        saveState: true,
        uniqueKey: 'id',
        customFilters: ['clients', 'start', 'end', 'clientType', 'locked', 'invoiceState', 'hideReportsWithZeros'],
        sortable: ['updated_at', 'client', 'start_date', 'end_date', 'report_type', 'client_type', 'total_value', 'locked', 'invoiced', 'payed'],
        headings: {
          updated_at: this.$t('reports.reports.table.updated_at'),
          name: this.$t('reports.reports.table.name'),
          client: this.$t('reports.reports.table.client'),
          client_type: this.$t('reports.reports.table.client_type'),
          start_date: this.$t('reports.reports.table.start_date'),
          end_date: this.$t('reports.reports.table.end_date'),
          total_value: this.$t('reports.reports.table.total_value'),
          locked: this.$t('reports.reports.table.state'),
          actions: this.$t('reports.reports.table.actions'),
          is_control: this.$t('reports.reports.table.is_control'),
          invoiced: this.$t('reports.reports.table.invoiced'),
          payed: this.$t('reports.reports.table.payed')
        },
        columnsClasses: {
          updated_at: 'updated_at',
          name: 'name',
          client: 'client',
          client_type: 'client_type',
          start_date: 'start_date',
          end_date: 'end_date',
          total_value: 'total_value',
          locked: 'locked',
          invoiced: 'invoiced',
          payed: 'payed',
          actions: 'actions',
          invoiceState: 'invoice-state'
        }
      },
      selectedReportForUpdate: undefined,
      selectedReportForDeletion: undefined,
      isBulkUploading: false,
      isBulkLocking: false
    }
  },
  mounted: function () {
    if (!this.filters.dateRange.start || !this.filters.dateRange.end) {
      this.filters.dateRange.start = this.moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD')
      this.filters.dateRange.end = this.moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    }

    this.$nextTick(() => {
      if (this.$refs.reportsTable) {
        this.$refs.reportsTable.setOrder('updated_at', false)
      }
    })
  },
  methods: {
    reload: function () {
      this.$refs.reportsTable.refresh()
    },
    onLoading: function () {
      this.$emit('loading')
    },
    onLoaded: function (state) {
      this.$emit('loaded', state)
    },
    loadFilters: function () {
      return new Promise((resolve, reject) => {
        Promise.all([this.loadClusters(), this.readableReportsEntities])
          .then(values => {
            this.entities = values[1].map(entity => {
              return {
                ...entity,
                uid: `e${entity.id}`
              }
            })

            this.clusters = values[0].map(cluster => {
              return {
                ...cluster,
                uid: `c${cluster.id}`,
                entitiesWithPermissions: cluster.entitiesIds.filter(entityId => this.readableReportsEntities.some(entity => entity.id === entityId)).length
              }
            }).sort((a, b) => b.entitiesIds.length - a.entitiesIds.length)

            if (this.clusters.length > 0) {
              this.clients.push({
                name: this.$t('reports.templates.labels.multiselect.clusters').toUpperCase(),
                $isDisabled: true
              })
              this.clients = this.clients.concat(this.clusters)
              this.clients.push({
                name: this.$t('reports.reports.labels.multiselect.entities').toUpperCase(),
                $isDisabled: true
              })
            }

            this.clients = this.clients.concat(this.entities)

            resolve()
          })
          .catch(errors => reject(errors))
      })
    },
    download: function (report) {
      ReportsService.downloadExcel(report.id)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${report.name}_${report.client.name}.xlsx`)
          document.body.appendChild(link)
          link.click()
        }).catch(errors => this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions))
    },
    update: function (report) {
      if (this.canUpdateReport(report)) {
        this.selectedReportForUpdate = report
      }
    },
    presentClient: function (client) {
      return client.cities.length === 0 ? client.name : `${client.name} (${client.cities.join(',')})`
    },
    presentTotalValue: function (value) {
      return value === 0 ? String(0) : String(value / 100)
    },
    onReportUpdate: function (data) {
      let index = this.$refs.reportsTable.data.findIndex(element => element.id === data.id)
      this.$refs.reportsTable.data[index] = data
      this.selectedReportForUpdate = undefined
      this.$refs.reportsTable.refresh()
    },
    updateModalWasClosed: function () {
      this.selectedReportForUpdate = undefined
    },
    onDeleteReportModalWasClosed: function () {
      this.selectedReportForDeletion = undefined
    },
    canUpdateReport: function (report) {
      return this.writableReportsEntities.some(entity => entity.id === report.client.id)
    },
    canDeleteReport: function (report) {
      return !report.locked && this.canUpdateReport(report)
    },
    deleteReport: function (report) {
      this.selectedReportForDeletion = report
    },
    confirmDelete: function () {
      ReportsService.delete(this.selectedReportForDeletion.id)
        .then(response => {
          this.$refs.reportsTable.refresh()
          this.showToast(this.$t('reports.reports.messages.successful_delete'), this.$store.state.successToastOptions)
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('reports.reports.messages.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.selectedReportForDeletion = undefined
        })
    },
    cancelDelete: function () {
      this.selectedReportForDeletion = undefined
    },
    onBulkLockSuccessful: function () {
      this.$refs.reportsTable.refresh()
    },
    onBulkLockModalWasClosed: function () {
      this.isBulkLocking = false
    },

  },
  computed: {
    ...mapGetters({
      clusters: 'clusters'
    }),
    clientOptions: function () {
      return this.createClustersAndEntitiesList(this.clusters, this.readableReportsEntities)
    },
    columns: function () {
      let columns = ['updated_at', 'name', 'client', 'start_date', 'end_date', 'total_value', 'invoiced', 'payed', 'actions']
      if (this.canUpdateReports) {
        columns.splice(5, 0, 'client_type')
        columns.splice(6, 0, 'is_control')
        columns.splice(8, 0, 'locked')
      }
      return columns
    },
    activeFilters: function () {
      let numFilters = 0
      Object.keys(this.filters).forEach(filter => {
        if (filter === 'dateRange' && this.filters.dateRange.start && this.filters.dateRange.end) {
          return ++numFilters
        } else if (filter !== 'querySearch' && ((Array.isArray(this.filters[filter]) && this.filters[filter].length > 0) || (!Array.isArray(this.filters[filter]) && this.filters[filter] !== null))) {
          return ++numFilters
        }
      })
      return numFilters
    },
    reportsService: function () {
      return this.$store.state.services.find(service => service.url === this.serviceUrl)
    },
    canUpdateReports: function () {
      return this.writableReportsEntities.length > 0
    },
    readableReportsEntities: function () {
      let readableEntities = []
      if (this.reportsService) {
        let readableReportPermissions = this.reportsService.user_permissions.filter(permission => permission.access_type >= 1 && permission.info_type === reportsInfoType)

        if (readableReportPermissions.length > 0) {
          if (readableReportPermissions.some(permission => permission.entity_id === null)) {
            readableEntities = this.reportsService.entities
          } else {
            readableReportPermissions.forEach(permission => {
              readableEntities.push(this.reportsService.entities.find(entity => entity.id === permission.entity_id))
            })
          }
        }
      }
      return readableEntities
    },
    writableReportsEntities: function () {
      if (this.reportsService) {
        let writableReportPermissions = this.reportsService.user_permissions.filter(permission => permission.access_type >= 2 && permission.info_type === reportsInfoType)

        if (writableReportPermissions.length > 0) {
          let writableEntities = []

          if (writableReportPermissions[0].entity_id === null) {
            writableEntities = this.reportsService.entities
          } else {
            writableReportPermissions.forEach(permission => {
              writableEntities.push(this.reportsService.entities.find(entity => entity.id === permission.entity_id))
            })
          }
          return writableEntities
        }
      }
      return []
    },
  },
  watch: {
    querySearch: {
      handler: function (value) {
        this.filters.querySearch = value || ''
      },
      immediate: true
    },
    customQueries: {
      handler: function (value) {
        if (value.start && value.start !== '') {
          this.filters.dateRange.start = value.start
        }
        if (value.end && value.end !== '') {
          this.filters.dateRange.end = value.end
        }
        if (Array.isArray(value.selectedEntityUids) && value.selectedEntityUids) {
          this.filters.clients = this.reloadSelectedClustersAndEntities(this.clientOptions, value.selectedEntityUids)
        }
        if (value.clientType && value.clientType !== '') {
          this.filters.clientType = this.clientTypes.find(element => element.val === value.clientType)
        }
        if (value.invoiceState && value.invoiceState !== '') {
          this.filters.invoiceState = this.invoiceStates.find(element => element.val === parseInt(value.invoiceState)) || null
        }
        if (has(value, 'locked') && value.locked !== '') {
          this.filters.locked = this.reportStates.find(element => element.val === value.locked)
        }
        if (has(value, 'hideReportsWithZeros') && value.hideReportsWithZeros !== '') {
          this.filters.hideReportsWithZeros = value.hideReportsWithZeros
        }
      },
      immediate: true
    },
    'filters.querySearch': function (value) {
      this.$refs.reportsTable.setFilter(value)
    },
    'filters.dateRange': function (value) {
      this.$refs.reportsTable.customQueries.start = value.start || ''
      this.emitTableFilter('vue-tables.reportsTable.filter::end', value.end || '')
    },
    'filters.clients': function (options) {
      const selectedOptions = this.getSelectedOptionsIdsAndUids(options)
      this.emitTableFilter('vue-tables.reportsTable.filter::clients', Array.from(selectedOptions.ids).join(','))
      this.$nextTick(() => {
        let state = LocalStorageHelper.get(TABLE_REPORTS)
        state.customQueries.selectedEntityUids = Array.from(selectedOptions.uids)
        LocalStorageHelper.set(TABLE_REPORTS, state)
      })
    },
    'filters.clientType': function (value) {
      this.emitTableFilter('vue-tables.reportsTable.filter::clientType', value ? value.val : undefined)
    },
    'filters.invoiceState': function (value) {
      this.emitTableFilter('vue-tables.reportsTable.filter::invoiceState', value ? value.val : undefined)
    },
    'filters.locked': function (value) {
      this.emitTableFilter('vue-tables.reportsTable.filter::locked', value ? value.val : undefined)
    },
    'filters.hideReportsWithZeros': function (value) {
      console.log("HERE")
      this.emitTableFilter('vue-tables.reportsTable.filter::hideReportsWithZeros', value )
    },
    activeFilters: function (nrFilters) {
      this.$emit('filters-updated', nrFilters)
    }
  }
}
</script>

<style lang="scss">

.total_value {
  text-align: center;
}

.locked {
  text-align: center;
}

.invoiced {
  text-align: center;
}

.payed {
  text-align: center;
}

.actions {
  text-align: center;
}

</style>
